import React, { useMemo, useState } from 'react';
import { Tabs } from 'antd';
import styles from './s.module.less';
import QuestionCard from '../QuestionCard';
import QuestionEditModal from '../QuestionEditModal';
import MatchingProvider from '../MatchingProvider';
import MatchingProviderEditModal from '../MatchingProviderEditModal';
import { IClosedFunnelFilter, IClosedFunnelQuestion } from 'types/operation';

interface BlocksTabProps {
    questions?: IClosedFunnelQuestion[];
    filters?: IClosedFunnelFilter[];
    secondTab: string;
    setSecondTab: (key: string) => void;
    onSuccess?: () => void;
}

const TreatmentOptions = [
    { label: 'Medication management', value: 'prescribers' },
    { label: 'Therapy and Counseling', value: 'therapist' },
];

const BlocksTab: React.FC<BlocksTabProps> = ({ questions, filters, secondTab, setSecondTab, onSuccess }) => {
    const [subTab, setSubTab] = useState('active');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [mpEditModalOpen, setMpEditModalOpen] = useState(false);
    const [mpEditData, setMpEditData] = useState<any>(null);

    const activeQuestions = useMemo(() => questions?.filter((q) => q.status === 'A') || [], [questions]);
    const inactiveQuestions = useMemo(() => questions?.filter((q) => q.status === 'D') || [], [questions]);

    const handleEdit = (q: any) => {
        setEditData(q);
        setEditModalOpen(true);
    };

    const handleSuccess = () => {
        setEditModalOpen(false);
        onSuccess?.();
    };

    const handleCancel = () => {
        setEditModalOpen(false);
    };

    const handleMpEdit = (item: any) => {
        setMpEditData(item);
        setMpEditModalOpen(true);
    };

    const handleMpSuccess = () => {
        setMpEditModalOpen(false);
        onSuccess?.();
    };

    const handleMpCancel = () => {
        setMpEditModalOpen(false);
    };

    const emptyRender = (emptyText: string) => {
        return (
            <div className={styles.emptyRender}>
                <div className={styles.emptyText}>{emptyText}</div>
                <div className={styles.emptyImg} />
            </div>
        );
    };

    return (
        <div className={styles.customTabsWrapper}>
            <div className={styles.customTabs}>
                <div
                    className={secondTab === 'questions' ? styles.tabActive : styles.tab}
                    onClick={() => setSecondTab('questions')}
                >
                    Questions
                </div>
                <div
                    className={secondTab === 'matching' ? styles.tabActive : styles.tab}
                    onClick={() => setSecondTab('matching')}
                >
                    Matching provider
                </div>
            </div>
            {secondTab === 'questions' && (
                <Tabs
                    activeKey={subTab}
                    onChange={setSubTab}
                    className={styles.antdSubTabs}
                    items={[
                        {
                            key: 'active',
                            label: `Active questions (${activeQuestions.length})`,
                            children: (
                                <>
                                    {
                                        activeQuestions && activeQuestions.length > 0 ? activeQuestions.map((q) => (
                                            <QuestionCard
                                                key={q.id}
                                                question={q.name}
                                                subtext={q.subtext}
                                                type={q.type}
                                                onEdit={() => handleEdit(q)}
                                            />
                                        )) : emptyRender('No active questions')
                                    }
                                </>
                            ),
                        },
                        {
                            key: 'inactive',
                            label: `Inactive questions (${inactiveQuestions.length})`,
                            children: (
                                <>
                                    {
                                        inactiveQuestions && inactiveQuestions.length > 0 ? inactiveQuestions.map((q) => (
                                            <QuestionCard
                                                key={q.id}
                                                question={q.name}
                                                subtext={q.subtext}
                                                type={q.type}
                                                onEdit={() => handleEdit(q)}
                                            />
                                        )) : emptyRender('No inactive questions')
                                    }
                                </>
                            ),
                        },
                    ]}
                />
            )}
            {secondTab === 'matching' && (
                <>
                    {filters?.map((item) => (
                        <MatchingProvider
                            key={item.id}
                            treatmentOptions={TreatmentOptions}
                            treatment={item.filterValues}
                            onEdit={() => handleMpEdit(item)}
                        />
                    ))}
                </>
            )}
            {editModalOpen && editData && (
                <QuestionEditModal
                    open={editModalOpen}
                    question={editData}
                    onSuccess={handleSuccess}
                    onCancel={handleCancel}
                />
            )}
            {mpEditModalOpen && mpEditData && (
                <MatchingProviderEditModal
                    open={mpEditModalOpen}
                    treatmentOptions={TreatmentOptions}
                    treatment={mpEditData}
                    onSuccess={handleMpSuccess}
                    onCancel={handleMpCancel}
                />
            )}
        </div>
    );
};

export default BlocksTab;
